import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';

const PaymentWaitingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  const [searchParams] = useSearchParams();
  const transactionNumber = searchParams.get('txn');
  
  const [status, setStatus] = useState<'pending' | 'success' | 'failed' | null>(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [transactionInfo, setTransactionInfo] = useState<any>(null);
  const [checkAttempts, setCheckAttempts] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    if (!transactionNumber) {
      navigate('/credits');
      return;
    }

    checkTransactionStatus();

    // العد التنازلي
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // فحص حالة المعاملة كل ثانيتين
    const checkInterval = setInterval(() => {
      checkTransactionStatus();
    }, 2000);

    return () => {
      clearInterval(countdownInterval);
      clearInterval(checkInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionNumber, user, navigate]);

  const checkTransactionStatus = async () => {
    setCheckAttempts(prev => prev + 1);
    
    try {
      // Query مباشرة من الجدول لأن الدالة قد تعيد JSON معقد
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('transaction_number', transactionNumber)
        .single();

      if (error && checkAttempts > 5) {
        console.error('Error fetching transaction:', error);
        setStatus('failed');
        setLoading(false);
        return;
      }

      if (data && data.status) {
        setTransactionInfo(data);
        setStatus(data.status);
        setLoading(false);

        // إذا كانت العملية مكتملة (نجحت أو فشلت)، توقف عن الفحص
        if (data.status === 'success' || data.status === 'failed') {
          setCountdown(0);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      if (checkAttempts > 10) {
        setStatus('failed');
        setLoading(false);
      }
    }
  };

  const handleBackToCredits = () => {
    navigate('/credits');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <AppHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="text-center">
          {loading && (
            <>
              <div className="mb-8">
                <div className="inline-block animate-spin rounded-full h-24 w-24 border-b-2 border-blue-500"></div>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                {t('pages.paymentWaiting.waiting')}
              </h1>
              <p className="text-gray-300 text-lg mb-4">{t('pages.paymentWaiting.dontClose')}</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                {t('pages.paymentWaiting.checking')}
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                {t('pages.paymentWaiting.transactionNumber')}: <span className="text-blue-400 font-bold">{transactionNumber}</span>
              </p>
              <p className="text-gray-500 text-sm">
                {t('pages.paymentWaiting.checkingIn', { seconds: countdown })}
              </p>
            </>
          )}

          {status === 'success' && transactionInfo && (
            <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border-2 border-green-500 rounded-2xl p-8 mt-8">
              <div className="mb-6">
                <div className="inline-block bg-green-500 rounded-full p-4 mb-4">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  {t('pages.paymentWaiting.success.title')}
                </h2>
                <p className="text-green-300 text-lg">
                  {t('pages.paymentWaiting.success.message')}
                </p>
              </div>

              <div className="bg-zinc-900 rounded-xl p-6 mb-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{t('pages.paymentWaiting.transactionNumber')}:</span>
                    <span className="text-white font-bold">#{transactionNumber}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{t('pages.paymentWaiting.amount')}:</span>
                    <span className="text-white font-bold">${transactionInfo.price_amount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{t('pages.paymentWaiting.creditsAdded')}:</span>
                    <span className="text-yellow-400 font-bold">{transactionInfo.credits_amount} {t('pages.paymentWaiting.credits')}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleGoToDashboard}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl transition-all shadow-xl hover:shadow-2xl"
                >
                  {t('pages.paymentWaiting.goToDashboard')}
                </button>
                <button
                  onClick={handleBackToCredits}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-4 px-8 rounded-xl transition-all"
                >
                  {t('pages.paymentWaiting.buyMore')}
                </button>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div className="bg-gradient-to-br from-red-900/20 to-orange-900/20 border-2 border-red-500 rounded-2xl p-8 mt-8">
              <div className="mb-6">
                <div className="inline-block bg-red-500 rounded-full p-4 mb-4">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  {t('pages.paymentWaiting.failed.title')}
                </h2>
                <p className="text-red-300 text-lg">
                  {t('pages.paymentWaiting.failed.message')}
                </p>
              </div>

              {transactionInfo && (
                <div className="bg-zinc-900 rounded-xl p-6 mb-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">{t('pages.paymentWaiting.transactionNumber')}:</span>
                      <span className="text-white font-bold">#{transactionNumber}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">{t('pages.paymentWaiting.amount')}:</span>
                      <span className="text-white font-bold">${transactionInfo.price_amount}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleBackToCredits}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl transition-all shadow-xl hover:shadow-2xl"
                >
                  {t('pages.paymentWaiting.tryAgain')}
                </button>
                <button
                  onClick={handleGoToDashboard}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-4 px-8 rounded-xl transition-all"
                >
                  {t('pages.paymentWaiting.backToDashboard')}
                </button>
              </div>
            </div>
          )}

          {status === 'pending' && countdown === 0 && (
            <div className="bg-yellow-900/20 border-2 border-yellow-500 rounded-2xl p-8 mt-8">
              <div className="mb-6">
                <div className="inline-block bg-yellow-500 rounded-full p-4 mb-4">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  {t('pages.paymentWaiting.pending.title')}
                </h2>
                <p className="text-yellow-300 text-lg mb-4">
                  {t('pages.paymentWaiting.transactionNumber')}: #{transactionNumber}
                </p>
                <p className="text-gray-400 text-sm">
                  {t('pages.paymentWaiting.pending.message')}
                </p>
              </div>

              <button
                onClick={handleBackToCredits}
                className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-4 px-8 rounded-xl transition-all"
              >
                {t('pages.paymentWaiting.backToCredits')}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PaymentWaitingPage;

